import { useState } from "react";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { toolCategories, formatCurrency, formatPercent } from "../../../data/PromotionIQ/PromotionData";
import { Layers, DollarSign, TrendingUp, Zap } from "lucide-react";
import React from "react";

const ShopperInfluenceStack = () => {
  const [selectedTools, setSelectedTools] = useState<string[]>(["display", "paid_social", "geo_fencing"]);

  // Get selected tool details
  const selectedToolDetails = toolCategories
    .flatMap(c => c.tools)
    .filter(t => selectedTools.includes(t.id));

  // Calculate cumulative lift
  const cumulativeLift = selectedToolDetails.reduce((acc, tool) => acc * (1 + tool.lift_contribution), 1);
  const totalCostMultiplier = selectedToolDetails.reduce((acc, tool) => acc + tool.cost_multiplier - 1, 1);

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Shopper Influence Stack"
        question="Which mechanisms are actually driving demand?"
      />

      {/* Stack Visualization */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="border border-border bg-card p-6">
          <h3 className="section-header mb-4">Influence Layer Stack</h3>
          
          {/* Base Pricing Layer */}
          <div className="mb-4">
            <div className="p-4 bg-muted/50 border-2 border-primary">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <span className="font-bold">PRICING</span>
                </div>
                <span className="text-sm text-muted-foreground">Always Present</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Base layer - price reduction is the foundation of every promotion
              </p>
            </div>
          </div>

          {/* Tool Layers */}
          <div className="space-y-2">
            {selectedToolDetails.map((tool, index) => (
              <div
                key={tool.id}
                className="p-4 border border-border bg-gradient-to-r from-primary/10 to-transparent"
                style={{ marginLeft: `${(index + 1) * 8}px` }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-primary" />
                    <span className="font-medium">{tool.name}</span>
                  </div>
                  <span className="text-success font-bold">+{(tool.lift_contribution * 100).toFixed(0)}%</span>
                </div>
                <div className="flex items-center justify-between mt-2 text-sm text-muted-foreground">
                  <span>Cost: {tool.cost_multiplier}x</span>
                  <span>Hist. Eff: {formatPercent(tool.historical_effectiveness)}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Cumulative Summary */}
          <div className="mt-6 p-4 border-2 border-success bg-success/10">
            <div className="flex items-center justify-between">
              <span className="font-bold text-success">CUMULATIVE LIFT</span>
              <span className="text-2xl font-bold text-success">{((cumulativeLift - 1) * 100).toFixed(0)}%</span>
            </div>
          </div>
        </div>

        {/* Tool Detail Cards */}
        <div className="border border-border bg-card p-6">
          <h3 className="section-header mb-4">Tool Layer Details</h3>
          
          <div className="space-y-4">
            {selectedToolDetails.map((tool) => (
              <div key={tool.id} className="border border-border bg-muted/20 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-primary font-bold">{tool.name}</span>
                  <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground">{tool.category}</span>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-muted/30">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <DollarSign className="h-3 w-3" />
                      <span>Cost</span>
                    </div>
                    <p className="text-lg font-bold">{tool.cost_multiplier}x</p>
                  </div>
                  <div className="p-3 bg-muted/30">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <TrendingUp className="h-3 w-3" />
                      <span>Incrementality</span>
                    </div>
                    <p className="text-lg font-bold text-success">+{(tool.lift_contribution * 100).toFixed(0)}%</p>
                  </div>
                  <div className="p-3 bg-muted/30">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Zap className="h-3 w-3" />
                      <span>Elasticity Interaction</span>
                    </div>
                    <p className="text-lg font-bold">{tool.elasticity_interaction}x</p>
                  </div>
                  <div className="p-3 bg-muted/30">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Layers className="h-3 w-3" />
                      <span>Historical Eff.</span>
                    </div>
                    <p className="text-lg font-bold">{formatPercent(tool.historical_effectiveness)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tool Selection */}
      <div className="border border-border bg-card p-6">
        <h3 className="section-header mb-4">Configure Stack</h3>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {toolCategories.flatMap(c => c.tools).map((tool) => (
            <label
              key={tool.id}
              className={`flex items-center gap-2 p-3 border cursor-pointer ${
                selectedTools.includes(tool.id)
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50"
              }`}
            >
              <input
                type="checkbox"
                checked={selectedTools.includes(tool.id)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedTools([...selectedTools, tool.id]);
                  } else {
                    setSelectedTools(selectedTools.filter(t => t !== tool.id));
                  }
                }}
                className="sr-only"
              />
              <div className={`h-4 w-4 border-2 flex items-center justify-center ${
                selectedTools.includes(tool.id) ? "border-primary bg-primary" : "border-muted-foreground"
              }`}>
                {selectedTools.includes(tool.id) && (
                  <span className="text-primary-foreground text-xs">✓</span>
                )}
              </div>
              <span className="text-sm">{tool.name}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Key Insight */}
      <div className="border border-primary/50 bg-primary/5 p-6">
        <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
        <p className="text-lg">
          This reframes promotions as <span className="text-primary font-bold">shopper influence design</span>, not just tactics. 
          Each layer contributes to cumulative demand generation.
        </p>
      </div>
    </div>
  );
};

export default ShopperInfluenceStack;
